import { useState, useMemo, useEffect } from 'react';
import { Icon, Button, Badge, Spinner } from '@/components/atoms';
import { Input } from '@kisanlink/ui-commons';
import { Modal, Pagination } from '@/components/molecules';
import { CollaboratorForm } from '@/components/organisms';
import { useCollaboratorsList, useCollaboratorStats } from '@/hooks/useErpApi';
import type { CollaboratorResponse } from '@kisanlink/erp-service';

interface Member {
  id: string;
  name: string;
  email: string;
  phone: string;
  role?: string;
  status: 'active' | 'inactive';
  joinDate: string;
}

export default function CollaboratorsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedCollaborator, setSelectedCollaborator] = useState<CollaboratorResponse | null>(
    null
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Calculate offset for API call
  const offset = (currentPage - 1) * itemsPerPage;
  const { data: collaborators, isLoading, error, refetch } = useCollaboratorsList({
    limit: itemsPerPage,
    offset,
  });

  // Stats for selected collaborator (fetched when view modal is opened)
  const {
    data: collaboratorStats,
    isLoading: collaboratorStatsLoading,
    error: collaboratorStatsError,
    refetch: refetchCollaboratorStats,
  } = useCollaboratorStats(selectedCollaborator?.id || '');

  useEffect(() => {
    if (isDetailModalOpen && selectedCollaborator?.id) {
      refetchCollaboratorStats();
    }
  }, [isDetailModalOpen, selectedCollaborator?.id, refetchCollaboratorStats]);

  // Transform API data to UI format
  const allMembers: Member[] = useMemo(() => {
    return (collaborators || []).map((collab: CollaboratorResponse) => {
      const status: 'active' | 'inactive' = collab.is_active ? 'active' : 'inactive';

      return {
        id: collab.id,
        name: collab.company_name, // Use company_name instead of name
        email: collab.email || 'N/A',
        phone: collab.contact_number || 'N/A', // Use contact_number instead of phone
        status,
        joinDate: collab.created_at,
      };
    });
  }, [collaborators]);

  // Client-side filtering for search
  const filteredMembers = useMemo(() => {
    return allMembers.filter((member) => {
      const matchesSearch =
        member.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        member.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        member.phone.includes(searchQuery);
      return matchesSearch;
    });
  }, [allMembers, searchQuery]);

  // Get total count from API response if available, otherwise use filtered length
  const totalMembers = (collaborators as any)?.total_count || filteredMembers.length;
  
  // Reset to page 1 when search changes
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleAddMember = () => {
    setIsAddModalOpen(true);
  };

  const handleViewMember = (memberId: string) => {
    const collaborator = collaborators?.find((c) => c.id === memberId);
    if (collaborator) {
      setSelectedCollaborator(collaborator);
      setIsDetailModalOpen(true);
    }
  };

  const handleEditMember = (memberId: string) => {
    const collaborator = collaborators?.find((c) => c.id === memberId);
    if (collaborator) {
      setSelectedCollaborator(collaborator);
      setIsEditModalOpen(true);
    }
  };

  const handleFormSuccess = () => {
    setIsAddModalOpen(false);
    setIsEditModalOpen(false);
    setSelectedCollaborator(null);
    refetch();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <Icon name="Users" size={48} className="text-neutral-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-neutral-600">Loading collaborators...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-error-50 border border-error-200 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="bg-error-100 p-2 rounded-lg">
              <Icon name="TriangleAlert" size={24} className="text-error-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-error-900 mb-1">
                Failed to Load Collaborators
              </h3>
              <p className="text-error-700 mb-3">
                Unable to fetch collaborators data. Please try again.
              </p>
              <Button variant="primary" size="sm" onClick={() => refetch()}>
                <Icon name="RefreshCw" size={16} className="mr-2" />
                Retry
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const getStatusBadge = (status: Member['status']) => {
    return status === 'active' ? (
      <Badge variant="success">Active</Badge>
    ) : (
      <Badge variant="secondary">Inactive</Badge>
    );
  };

  const activeMembers = allMembers.filter((m) => m.status === 'active').length;

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-neutral-900">Collaborators</h1>
          <p className="text-neutral-600 mt-2">Manage FPO suppliers and vendors</p>
        </div>
        <Button variant="primary" className="sm:w-auto" onClick={handleAddMember}>
          <Icon name="UserPlus" size={20} className="mr-2" />
          Add Member
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-neutral-600">Total Collaborators</p>
            <div className="bg-primary-50 p-2 rounded-lg">
              <Icon name="Users" size={20} className="text-primary-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-neutral-900">{totalMembers}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-neutral-600">Active Collaborators</p>
            <div className="bg-success-50 p-2 rounded-lg">
              <Icon name="UserCheck" size={20} className="text-success-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-success-700">{activeMembers}</p>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Icon
                name="Search"
                size={20}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400"
              />
              <Input
                type="text"
                placeholder="Search by name, email, or phone..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Members Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredMembers.map((member) => {
          const collaborator = collaborators?.find((c) => c.id === member.id);
          return (
            <div
              key={member.id}
              className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6 hover:shadow-md transition-shadow duration-200"
            >
              <div className="flex items-start gap-4">
                {collaborator?.logo ? (
                  <div className="h-16 w-16 flex-shrink-0 rounded-full overflow-hidden bg-neutral-100 border-2 border-neutral-200">
                    <img
                      src={collaborator.logo}
                      alt={`${member.name} logo`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        // Fallback to initials if image fails to load
                        e.currentTarget.style.display = 'none';
                        const parent = e.currentTarget.parentElement;
                        if (parent) {
                          parent.className =
                            'h-16 w-16 flex-shrink-0 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center text-white font-bold text-lg';
                          parent.textContent = getInitials(member.name);
                        }
                      }}
                    />
                  </div>
                ) : (
                  <div className="h-16 w-16 flex-shrink-0 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                    {getInitials(member.name)}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-1">
                    <h3 className="text-lg font-semibold text-neutral-900 truncate">
                      {member.name}
                    </h3>
                    {getStatusBadge(member.status)}
                  </div>
                </div>
              </div>

              <div className="mt-4 space-y-2">
                <div className="flex items-center gap-2 text-sm text-neutral-600">
                  <Icon name="Mail" size={16} className="text-neutral-400" />
                  <span className="truncate">{member.email}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-neutral-600">
                  <Icon name="Phone" size={16} className="text-neutral-400" />
                  <span>{member.phone}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-neutral-600">
                  <Icon name="Calendar" size={16} className="text-neutral-400" />
                  <span>Joined {new Date(member.joinDate).toLocaleDateString()}</span>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-neutral-200">
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    className="flex-1"
                    onClick={() => handleViewMember(member.id)}
                  >
                    <Icon name="Eye" size={16} className="mr-1" />
                    View
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    className="flex-1"
                    onClick={() => handleEditMember(member.id)}
                  >
                    <Icon name="Pencil" size={16} className="mr-1" />
                    Edit
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200">
        <Pagination
          currentPage={currentPage}
          totalItems={totalMembers}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={setItemsPerPage}
          disabled={isLoading}
        />
      </div>

      {/* Role Distribution */}
      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
        <h2 className="text-lg font-semibold text-neutral-900 mb-4">Role Distribution</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="flex items-center gap-3 p-4 bg-error-50 rounded-lg">
            <Icon name="Shield" size={24} className="text-error-600" />
            <div>
              <p className="text-sm text-neutral-600">Admins</p>
              <p className="text-xl font-bold text-neutral-900">
                {allMembers.filter((m) => m.role === 'admin').length}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-4 bg-warning-50 rounded-lg">
            <Icon name="Briefcase" size={24} className="text-warning-600" />
            <div>
              <p className="text-sm text-neutral-600">Coordinators</p>
              <p className="text-xl font-bold text-neutral-900">
                {allMembers.filter((m) => m.role === 'coordinator').length}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-4 bg-info-50 rounded-lg">
            <Icon name="Users" size={24} className="text-info-600" />
            <div>
              <p className="text-sm text-neutral-600">Managers</p>
              <p className="text-xl font-bold text-neutral-900">
                {allMembers.filter((m) => m.role === 'manager').length}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-4 bg-primary-50 rounded-lg">
            <Icon name="User" size={24} className="text-primary-600" />
            <div>
              <p className="text-sm text-neutral-600">Members</p>
              <p className="text-xl font-bold text-neutral-900">
                {allMembers.filter((m) => m.role === 'member').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Add Collaborator Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        title="Add New Collaborator"
        size="lg"
      >
        <CollaboratorForm onSuccess={handleFormSuccess} onCancel={() => setIsAddModalOpen(false)} />
      </Modal>

      {/* Edit Collaborator Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedCollaborator(null);
        }}
        title="Edit Collaborator"
        size="lg"
      >
        {selectedCollaborator && (
          <CollaboratorForm
            initialData={{
              ...selectedCollaborator,
              gst_number: selectedCollaborator.gst_number || '',
            }}
            onSuccess={handleFormSuccess}
            onCancel={() => {
              setIsEditModalOpen(false);
              setSelectedCollaborator(null);
            }}
          />
        )}
      </Modal>

      {/* Collaborator Detail Modal with Stats */}
      <Modal
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedCollaborator(null);
        }}
        title={selectedCollaborator ? `${selectedCollaborator.company_name} - Details` : 'Collaborator Details'}
        size="lg"
      >
        {selectedCollaborator && (
          <CollaboratorDetailView
            collaborator={selectedCollaborator}
            stats={collaboratorStats as CollaboratorStats | undefined}
            statsLoading={collaboratorStatsLoading}
            statsError={collaboratorStatsError as Error | null}
          />
        )}
      </Modal>
    </div>
  );
}

// Collaborator Detail View Component with Stats
interface CollaboratorStats {
  collaborator_id: string;
  company_name: string;
  po_count: number;
  grn_count: number;
  total_amount: number;
  active_po_count: number;
  last_po_date: string | null;
}

function CollaboratorDetailView({
  collaborator,
  stats,
  statsLoading,
  statsError,
}: {
  collaborator: CollaboratorResponse;
  stats?: CollaboratorStats;
  statsLoading: boolean;
  statsError: Error | null;
}) {
  const typedStats = stats;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      {/* Basic Information */}
      <div className="bg-neutral-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-neutral-900 mb-4">Basic Information</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-neutral-600 mb-1">Company Name</p>
            <p className="text-base font-medium text-neutral-900">{collaborator.company_name}</p>
          </div>
          <div>
            <p className="text-sm text-neutral-600 mb-1">Contact Person</p>
            <p className="text-base font-medium text-neutral-900">{collaborator.contact_person || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-neutral-600 mb-1">Email</p>
            <p className="text-base font-medium text-neutral-900">{collaborator.email || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-neutral-600 mb-1">Phone</p>
            <p className="text-base font-medium text-neutral-900">{collaborator.contact_number || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-neutral-600 mb-1">GST Number</p>
            <p className="text-base font-medium text-neutral-900">{collaborator.gst_number || 'N/A'}</p>
          </div>
          <div>
            <p className="text-sm text-neutral-600 mb-1">Status</p>
            <Badge variant={collaborator.is_active ? 'success' : 'secondary'}>
              {collaborator.is_active ? 'Active' : 'Inactive'}
            </Badge>
          </div>
        </div>
      </div>

      {/* Transaction Statistics */}
      <div className="bg-white rounded-lg border border-neutral-200 p-6">
        <h3 className="text-lg font-semibold text-neutral-900 mb-4">Transaction Statistics</h3>
        {statsLoading ? (
          <div className="flex justify-center items-center py-8">
            <Spinner size="md" />
          </div>
        ) : statsError ? (
          <div className="bg-error-50 border border-error-200 rounded-lg p-4">
            <p className="text-error-700">Failed to load statistics. Please try again.</p>
          </div>
        ) : typedStats ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="bg-primary-50 rounded-lg p-4">
              <p className="text-sm text-neutral-600 mb-1">Total POs</p>
              <p className="text-2xl font-bold text-primary-700">{typedStats.po_count}</p>
            </div>
            <div className="bg-success-50 rounded-lg p-4">
              <p className="text-sm text-neutral-600 mb-1">Total GRNs</p>
              <p className="text-2xl font-bold text-success-700">{typedStats.grn_count}</p>
            </div>
            <div className="bg-warning-50 rounded-lg p-4">
              <p className="text-sm text-neutral-600 mb-1">Total Amount</p>
              <p className="text-xl font-bold text-warning-700">{formatCurrency(typedStats.total_amount)}</p>
            </div>
            <div className="bg-info-50 rounded-lg p-4">
              <p className="text-sm text-neutral-600 mb-1">Active POs</p>
              <p className="text-2xl font-bold text-info-700">{typedStats.active_po_count}</p>
            </div>
            {typedStats.last_po_date && (
              <div className="bg-neutral-50 rounded-lg p-4 col-span-2 md:col-span-1">
                <p className="text-sm text-neutral-600 mb-1">Last Order</p>
                <p className="text-base font-semibold text-neutral-900">{formatDate(typedStats.last_po_date)}</p>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
